import * as React from "react";
import _ from "lodash";
import { $, merge } from "glamor";
import { DATATEST } from "constants/integration";
import { isValidNumber } from "lib/number";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IProps {
    [key: string]: any;
    editable?: boolean;
    isZeroValueAllowed?: boolean;
}

enum EDIT_MODES {
    REPLACE,
    APPEND,
}

export class NumberCellEditor extends React.PureComponent<IProps, any> {
    private inputRef;
    private takeDefaultValue = false;
    private defaultValue = "";
    private isZeroValueAllowed;

    private styles = merge(
        $(" input", {
            textAlign: "right",
            width: "100%",
            height: "27px",
        })
    );
    constructor(props) {
        super(props);
        let editMode = EDIT_MODES.APPEND;
        let value = isValidNumber(props.value) ? props.value : "";
        this.defaultValue = value;
        this.isZeroValueAllowed = props.isZeroValueAllowed;
        const isPinned = props.node.rowPinned === "top" || props.column.pinned === "top";
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            value = "";
        } else if (props.charPress && isValidNumber(props.charPress)) {
            value = props.charPress;
            editMode = EDIT_MODES.REPLACE;
        } else if (isPinned && _.includes(props.column.colId, "_total_units")) {
            const { plan = new Map<string, any>() } = props.getPlanByDoor(props.colDef.colId.split("_")[0]);
            let key = "quantity";
            if (props.node.rowIndex === 1) {
                key = "wholesale_amount";
            } else if (props.node.rowIndex === 2) {
                key = "retail_amount";
            }

            value = plan.get(key) || "";
        }
        this.state = {
            value,
            editMode,
        };
    }

    componentDidMount() {
        this.focus();
    }

    // Check if cell should select all when starts editing
    selectAllOnStart() {
        const { charPress, eventKey } = this.props;
        if (eventKey === KEYBOARD_KEYS.ENTER) {
            // On ENTER press
            return true;
        } else if (!eventKey && !charPress) {
            // On mouse double click or focused on tab
            return true;
        }

        return false;
    }

    focus() {
        const startIndex = !this.selectAllOnStart() ? this.state.value.toString().length : 0;
        const endIndex = isValidNumber(this.state.value) ? this.state.value.toString().length : 0;
        if (this.inputRef) {
            (this.inputRef as any).focus();
            (this.inputRef as any).setSelectionRange(startIndex, endIndex);
        }
    }

    getValue() {
        const value = this.takeDefaultValue ? this.defaultValue : this.state.value;
        if (this.isZeroValueAllowed) {
            return value;
        }
        const num = Number(value);
        return isNaN(num) || value === "" ? value : num;
    }

    handleChange = (event) => {
        // TODO: Get currency from cellRendererParams instead of pinnedRowCellRendererParams
        let decimalPlaces = this.props.colDef?.cellEditorParams?.currency?.decimal_places;

        if (decimalPlaces === undefined) {
            decimalPlaces = 2;
        }
        const newValue = event.target.value;

        if (newValue === "" || new RegExp(`^(\\d+)?(\\.\\d{0,${decimalPlaces}})?$`).test(newValue)) {
            this.setState({ value: newValue });
        }
    };

    setRef = (ref) => {
        this.inputRef = ref;
    };

    render() {
        return (
            <div {...this.styles} onKeyDown={this.handleKeyDown}>
                <input
                    data-test={DATATEST.ASSORTMENTS.GRID.NUMBER_CELL_EDITOR}
                    ref={this.setRef}
                    type="text"
                    value={this.state.value}
                    onChange={this.handleChange}
                />
            </div>
        );
    }

    private handleKeyDown = (e: React.KeyboardEvent) => {
        const { editMode } = this.state;
        const { api } = this.props;
        const currentCell = api.getEditingCells()[0];

        switch (e.key) {
            case KEYBOARD_KEYS.UP:
            case KEYBOARD_KEYS.DOWN:
                if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) {
                    let value = Number(this.state.value);
                    if (!isNaN(value)) {
                        value = e.key === KEYBOARD_KEYS.UP ? value + 1 : value - 1;
                        this.setState({ value });
                        process.nextTick(() => {
                            (this.inputRef as any).setSelectionRange(0, value.toString().length);
                        });
                        e.stopPropagation();
                    }
                    break;
                }
            // eslint-disable-next-line no-fallthrough
            case KEYBOARD_KEYS.LEFT:
            case KEYBOARD_KEYS.RIGHT:
                // Suggestions will cause defaultPrevented to be true if using key events
                if (!e.defaultPrevented && editMode === EDIT_MODES.REPLACE) {
                    this.props.stopEditing();
                    this.navigateToCell(e, currentCell);
                    e.preventDefault();
                }
                break;
            default:
        }
    };

    private navigateToCell = (e, currentCell) => {
        const { api } = this.props;
        if (currentCell) {
            api.setFocusedCell(currentCell.rowIndex, currentCell.column.getColId());
        }

        const lastRow = api.getLastDisplayedRow();

        const focusedCell = api.getFocusedCell();
        api.clearRangeSelection();
        switch (e.keyCode) {
            case KEYBOARD_KEYS.RIGHT:
                api.tabToNextCell();
                break;
            case KEYBOARD_KEYS.LEFT:
                api.tabToPreviousCell();
                break;
            case KEYBOARD_KEYS.UP:
                if (focusedCell.rowIndex > 0) {
                    api.setFocusedCell(Math.max(0, focusedCell.rowIndex - 1), focusedCell.column.getColId());
                }
                break;
            case KEYBOARD_KEYS.DOWN:
            case KEYBOARD_KEYS.ENTER:
                // covering "new line"
                if (e.ctrlKey) {
                    api.setFocusedCell(
                        Math.min(focusedCell.rowIndex + 1, lastRow),
                        focusedCell.column.getColId(),
                        null
                    );
                }
                break;
            default:
        }
    };
}
